import AgentService from '../../services/aiagent/agent.service'
import SessionService from '../../services/aiagent/session.service'
import ChatService from '../../services/aiagent/chat.service'
import ProjectService from '../../services/aiagent/project.service'
import KnowledgeBaseService from '../../services/aiagent/knowledgeBase.service'
import WorkflowService from '../../services/aiagent/workflow.service'
import FormService from '../../services/aiagent/form.service'
import IntegrationService from '../../services/aiagent/integration.service'
import ExecutionService from '../../services/aiagent/execution.service'
import AgentModel from '../../models/aiagent/AgentModel'
import SessionModel from '../../models/aiagent/ChatSessionModel'
import { MessageModel } from '../../models/aiagent/MessageModel'

export const aiAgent = {
  namespaced: true,

  state: {
    // Agents
    agents: [],
    currentAgent: null,
    agentsLoading: false,

    // Sessions - filtered by current agent
    sessions: [],
    currentSession: null,
    sessionsLoading: false,

    // Messages - for current session
    messages: [],
    messagesLoading: false,
    sendingMessage: false,

    // Chat UI State
    chatSidebarCollapsed: false,
    agentPanelCollapsed: true,
    typingMessage: '',
    messageDraft: '',

    // Settings (future)
    userApiKeys: {}, // { openai: 'sk-...', claude: '...' }
    defaultProvider: 'OpenAI',

    // Error handling
    error: null,
    lastError: null,

    // Projects
    projects: [],
    currentProject: null,
    projectsLoading: false,
    projectSessions: [],
    projectKBs: [],

    // NEW: Workflows
    workflows: [],
    currentWorkflow: null,
    workflowsLoading: false,
    workflowExecutions: [],

    // NEW: Forms
    forms: [],
    currentForm: null,
    formsLoading: false,
    formSubmissions: [],

    // NEW: Integrations
    integrations: [],
    currentIntegration: null,
    integrationsLoading: false,
    integrationLogs: [],

    // NEW: Dashboard
    dashboard: null,
    dashboardLoading: false,
    integrationHealth: []
  },

  mutations: {
    // ==================== EXISTING MUTATIONS ====================
    // Agent mutations
    SET_AGENTS (state, agents) {
      state.agents = agents.map(a => {
        const agent = new AgentModel()
        agent.loadModel(a)
        return agent
      })
    },

    SET_CURRENT_AGENT (state, agent) {
      if (agent) {
        const agentModel = new AgentModel()
        agentModel.loadModel(agent)
        state.currentAgent = agentModel
      } else {
        state.currentAgent = null
      }
    },

    UPDATE_AGENT (state, { id, updates }) {
      const index = state.agents.findIndex(a => a.id === id)
      if (index !== -1) {
        Object.assign(state.agents[index], updates)
      }
      if (state.currentAgent && state.currentAgent.id === id) {
        Object.assign(state.currentAgent, updates)
      }
    },

    REMOVE_AGENT (state, agentId) {
      state.agents = state.agents.filter(a => a.id !== agentId)
      if (state.currentAgent && state.currentAgent.id === agentId) {
        state.currentAgent = null
      }
    },

    SET_AGENTS_LOADING (state, loading) {
      state.agentsLoading = loading
    },

    // Session mutations
    SET_SESSIONS (state, sessions) {
      state.sessions = sessions.map(s => {
        const session = new SessionModel()
        session.loadModel(s)
        return session
      })
    },

    SET_CURRENT_SESSION (state, session) {
      if (session) {
        const sessionModel = new SessionModel()
        sessionModel.loadModel(session)
        state.currentSession = sessionModel
      } else {
        state.currentSession = null
      }
    },

    ADD_SESSION (state, session) {
      const sessionModel = new SessionModel()
      sessionModel.loadModel(session)
      state.sessions.unshift(sessionModel)
    },

    UPDATE_SESSION (state, { sessionId, updates }) {
      const index = state.sessions.findIndex(s => s.id === sessionId)
      if (index !== -1) {
        Object.assign(state.sessions[index], updates)
      }
      if (state.currentSession && state.currentSession.id === sessionId) {
        Object.assign(state.currentSession, updates)
      }
    },

    REMOVE_SESSION (state, sessionId) {
      state.sessions = state.sessions.filter(s => s.id !== sessionId)
      if (state.currentSession && state.currentSession.id === sessionId) {
        state.currentSession = null
        state.messages = []
      }
    },

    SET_SESSIONS_LOADING (state, loading) {
      state.sessionsLoading = loading
    },

    // Message mutations
    SET_MESSAGES (state, messages) {
      state.messages = messages.map(m => {
        const message = new MessageModel()
        message.loadModel(m)
        return message
      })
    },

    ADD_MESSAGE (state, message) {
      const messageModel = new MessageModel()
      messageModel.loadModel(message)
      state.messages.push(messageModel)
    },

    UPDATE_MESSAGE (state, { messageId, updates }) {
      const index = state.messages.findIndex(m => m.id === messageId)
      if (index !== -1) {
        Object.assign(state.messages[index], updates)
      }
    },

    CLEAR_MESSAGES (state) {
      state.messages = []
    },

    SET_MESSAGES_LOADING (state, loading) {
      state.messagesLoading = loading
    },

    SET_SENDING_MESSAGE (state, sending) {
      state.sendingMessage = sending
    },

    // UI mutations
    TOGGLE_CHAT_SIDEBAR (state) {
      state.chatSidebarCollapsed = !state.chatSidebarCollapsed
    },

    SET_CHAT_SIDEBAR_COLLAPSED (state, collapsed) {
      state.chatSidebarCollapsed = collapsed
    },

    TOGGLE_AGENT_PANEL (state) {
      state.agentPanelCollapsed = !state.agentPanelCollapsed
    },

    SET_AGENT_PANEL_COLLAPSED (state, collapsed) {
      state.agentPanelCollapsed = collapsed
    },

    SET_TYPING_MESSAGE (state, message) {
      state.typingMessage = message
    },

    SET_MESSAGE_DRAFT (state, draft) {
      state.messageDraft = draft
      // Auto-save to localStorage
      if (draft) {
        localStorage.setItem('aiAgentMessageDraft', draft)
      } else {
        localStorage.removeItem('aiAgentMessageDraft')
      }
    },

    // Settings mutations
    SET_USER_API_KEY (state, { provider, apiKey }) {
      state.userApiKeys = Object.assign({}, state.userApiKeys, {
        [provider]: apiKey
      })
      // Save to localStorage (encrypted in production)
      localStorage.setItem('aiAgentApiKeys', JSON.stringify(state.userApiKeys))
    },

    SET_DEFAULT_PROVIDER (state, provider) {
      state.defaultProvider = provider
      localStorage.setItem('aiAgentDefaultProvider', provider)
    },

    // Error mutations
    SET_ERROR (state, error) {
      state.error = error
      state.lastError = error
    },

    CLEAR_ERROR (state) {
      state.error = null
    },

    // Project mutations
    SET_PROJECTS (state, projects) {
      state.projects = projects
    },

    SET_CURRENT_PROJECT (state, project) {
      state.currentProject = project
    },

    ADD_PROJECT (state, project) {
      state.projects.push(project)
    },

    UPDATE_PROJECT (state, updatedProject) {
      const index = state.projects.findIndex(p => p.id === updatedProject.id)
      if (index !== -1) {
        state.projects.splice(index, 1, updatedProject)
      }
      if (state.currentProject && state.currentProject.id === updatedProject.id) {
        state.currentProject = updatedProject
      }
    },

    REMOVE_PROJECT (state, projectId) {
      state.projects = state.projects.filter(p => p.id !== projectId)
      if (state.currentProject && state.currentProject.id === projectId) {
        state.currentProject = null
      }
    },

    SET_PROJECTS_LOADING (state, loading) {
      state.projectsLoading = loading
    },

    SET_PROJECT_SESSIONS (state, sessions) {
      state.projectSessions = sessions.map(s => {
        const session = new SessionModel()
        session.loadModel(s)
        return session
      })
    },

    SET_PROJECT_KBS (state, kbs) {
      state.projectKBs = kbs
    },

    CLEAR_PROJECT_DATA (state) {
      state.currentProject = null
      state.projectSessions = []
      state.projectKBs = []
    },

    // ==================== NEW: WORKFLOW MUTATIONS ====================
    SET_WORKFLOWS (state, workflows) {
      state.workflows = workflows
    },

    SET_CURRENT_WORKFLOW (state, workflow) {
      state.currentWorkflow = workflow
    },

    ADD_WORKFLOW (state, workflow) {
      state.workflows.push(workflow)
    },

    UPDATE_WORKFLOW (state, updatedWorkflow) {
      const index = state.workflows.findIndex(w => w.id === updatedWorkflow.id)
      if (index !== -1) {
        state.workflows.splice(index, 1, updatedWorkflow)
      }
      if (state.currentWorkflow && state.currentWorkflow.id === updatedWorkflow.id) {
        state.currentWorkflow = updatedWorkflow
      }
    },

    REMOVE_WORKFLOW (state, workflowId) {
      state.workflows = state.workflows.filter(w => w.id !== workflowId)
      if (state.currentWorkflow && state.currentWorkflow.id === workflowId) {
        state.currentWorkflow = null
      }
    },

    SET_WORKFLOWS_LOADING (state, loading) {
      state.workflowsLoading = loading
    },

    SET_WORKFLOW_EXECUTIONS (state, executions) {
      state.workflowExecutions = executions
    },

    // ==================== NEW: FORM MUTATIONS ====================
    SET_FORMS (state, forms) {
      state.forms = forms
    },

    SET_CURRENT_FORM (state, form) {
      state.currentForm = form
    },

    ADD_FORM (state, form) {
      state.forms.push(form)
    },

    UPDATE_FORM (state, updatedForm) {
      const index = state.forms.findIndex(f => f.id === updatedForm.id)
      if (index !== -1) {
        state.forms.splice(index, 1, updatedForm)
      }
      if (state.currentForm && state.currentForm.id === updatedForm.id) {
        state.currentForm = updatedForm
      }
    },

    REMOVE_FORM (state, formId) {
      state.forms = state.forms.filter(f => f.id !== formId)
      if (state.currentForm && state.currentForm.id === formId) {
        state.currentForm = null
      }
    },

    SET_FORMS_LOADING (state, loading) {
      state.formsLoading = loading
    },

    SET_FORM_SUBMISSIONS (state, submissions) {
      state.formSubmissions = submissions
    },

    // ==================== NEW: INTEGRATION MUTATIONS ====================
    SET_INTEGRATIONS (state, integrations) {
      state.integrations = integrations
    },

    SET_CURRENT_INTEGRATION (state, integration) {
      state.currentIntegration = integration
    },

    ADD_INTEGRATION (state, integration) {
      state.integrations.push(integration)
    },

    UPDATE_INTEGRATION (state, updatedIntegration) {
      const index = state.integrations.findIndex(i => i.id === updatedIntegration.id)
      if (index !== -1) {
        state.integrations.splice(index, 1, updatedIntegration)
      }
      if (state.currentIntegration && state.currentIntegration.id === updatedIntegration.id) {
        state.currentIntegration = updatedIntegration
      }
    },

    REMOVE_INTEGRATION (state, integrationId) {
      state.integrations = state.integrations.filter(i => i.id !== integrationId)
      if (state.currentIntegration && state.currentIntegration.id === integrationId) {
        state.currentIntegration = null
      }
    },

    SET_INTEGRATIONS_LOADING (state, loading) {
      state.integrationsLoading = loading
    },

    SET_INTEGRATION_LOGS (state, logs) {
      state.integrationLogs = logs
    },

    // ==================== NEW: DASHBOARD MUTATIONS ====================
    SET_DASHBOARD (state, dashboard) {
      state.dashboard = dashboard
    },

    SET_DASHBOARD_LOADING (state, loading) {
      state.dashboardLoading = loading
    },

    SET_INTEGRATION_HEALTH (state, health) {
      state.integrationHealth = health
    }
  },

  actions: {
    // ==================== EXISTING ACTIONS ====================
    // Initialize
    async initialize ({ commit, dispatch }) {
      // Load saved settings
      const savedApiKeys = localStorage.getItem('aiAgentApiKeys')
      if (savedApiKeys) {
        try {
          const keys = JSON.parse(savedApiKeys)
          Object.entries(keys).forEach(([provider, apiKey]) => {
            commit('SET_USER_API_KEY', { provider, apiKey })
          })
        } catch (e) {
          console.error('Failed to load API keys:', e)
        }
      }

      const savedProvider = localStorage.getItem('aiAgentDefaultProvider')
      if (savedProvider) {
        commit('SET_DEFAULT_PROVIDER', savedProvider)
      }

      const savedDraft = localStorage.getItem('aiAgentMessageDraft')
      if (savedDraft) {
        commit('SET_MESSAGE_DRAFT', savedDraft)
      }

      // Load agents
      await dispatch('loadAgents')
    },

    // Agent actions
    async loadAgents ({ commit }) {
      commit('SET_AGENTS_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await AgentService.getAgents()
        commit('SET_AGENTS', response.data)
      } catch (error) {
        console.error('Error loading agents:', error)
        commit('SET_ERROR', 'Failed to load agents')
        throw error
      } finally {
        commit('SET_AGENTS_LOADING', false)
      }
    },

    async loadAgent ({ commit }, agentId) {
      commit('CLEAR_ERROR')

      try {
        const response = await AgentService.getAgentById(agentId)
        commit('SET_CURRENT_AGENT', response.data)
        return response.data
      } catch (error) {
        console.error('Error loading agent:', error)
        commit('SET_ERROR', 'Failed to load agent')
        throw error
      }
    },

    async createAgent ({ commit }, agentData) {
      commit('CLEAR_ERROR')

      try {
        const response = await AgentService.createAgent(agentData)
        const agent = response.data

        // Add to agents list
        const agents = [...this.state.aiAgent.agents]
        agents.push(agent)
        commit('SET_AGENTS', agents)

        return agent
      } catch (error) {
        console.error('Error creating agent:', error)
        commit('SET_ERROR', 'Failed to create agent')
        throw error
      }
    },

    async updateAgent ({ commit }, { id, data }) {
      commit('CLEAR_ERROR')

      try {
        const response = await AgentService.updateAgent(id, data)
        commit('UPDATE_AGENT', { id, updates: response.data })
        return response.data
      } catch (error) {
        console.error('Error updating agent:', error)
        commit('SET_ERROR', 'Failed to update agent')
        throw error
      }
    },

    async removeAgent ({ commit }, agentId) {
      commit('CLEAR_ERROR')

      try {
        await AgentService.deleteAgent(agentId)
        commit('REMOVE_AGENT', agentId)
      } catch (error) {
        console.error('Error deleting agent:', error)
        commit('SET_ERROR', 'Failed to delete agent')
        throw error
      }
    },

    // Session actions
    async loadSessions ({ commit }, agentId) {
      commit('SET_SESSIONS_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await SessionService.getSessions(agentId)
        commit('SET_SESSIONS', response.data)
      } catch (error) {
        console.error('Error loading sessions:', error)
        commit('SET_ERROR', 'Failed to load sessions')
      } finally {
        commit('SET_SESSIONS_LOADING', false)
      }
    },

    async loadSession ({ commit }, sessionId) {
      commit('SET_MESSAGES_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await SessionService.getSessionById(sessionId)
        commit('SET_CURRENT_SESSION', response.data)
        commit('SET_MESSAGES', response.data.messages || [])
      } catch (error) {
        console.error('Error loading session:', error)
        commit('SET_ERROR', 'Failed to load session')
        throw error
      } finally {
        commit('SET_MESSAGES_LOADING', false)
      }
    },

    async createSession ({ commit }, { agentId, projectId, title }) {
      commit('CLEAR_ERROR')
      try {
        const sessionData = {
          agentId,
          projectId: projectId || null,
          title: title || 'Chat ' + new Date().toLocaleString()
        }
        const response = await SessionService.createSession(sessionData)
        commit('ADD_SESSION', response.data)
        commit('SET_CURRENT_SESSION', response.data)
        commit('CLEAR_MESSAGES')
        return response.data
      } catch (error) {
        console.error('Error creating session:', error)
        commit('SET_ERROR', 'Failed to create session')
        throw error
      }
    },

    async updateSessionTitle ({ commit }, { sessionId, title }) {
      try {
        const response = await SessionService.updateSession(sessionId, { title })
        console.log(response)
        commit('UPDATE_SESSION', { sessionId, updates: { title } })
      } catch (error) {
        console.error('Error updating session title:', error)
        commit('SET_ERROR', 'Failed to update session title')
      }
    },

    async deleteSession ({ commit }, sessionId) {
      try {
        await SessionService.deleteSession(sessionId)
        commit('REMOVE_SESSION', sessionId)
      } catch (error) {
        console.error('Error deleting session:', error)
        commit('SET_ERROR', 'Failed to delete session')
        throw error
      }
    },

    // Message actions
    async sendMessage ({ commit, state }, messageContent) {
      if (!state.currentSession) {
        commit('SET_ERROR', 'No active session')
        return
      }

      commit('SET_SENDING_MESSAGE', true)
      commit('CLEAR_ERROR')
      commit('SET_MESSAGE_DRAFT', '') // Clear draft on send

      // Add user message immediately for better UX
      const tempUserMessage = new MessageModel({
        role: 'user',
        content: messageContent,
        createdAt: new Date().toISOString(),
        isStreaming: false
      })
      commit('ADD_MESSAGE', tempUserMessage)

      try {
        // Send to API
        const response = await ChatService.sendMessage(
          state.currentSession.id,
          { content: messageContent, stream: false }
        )

        // Add AI response
        commit('ADD_MESSAGE', response.data)

        // Update session info
        commit('UPDATE_SESSION', {
          sessionId: state.currentSession.id,
          updates: {
            messageCount: state.messages.length,
            lastMessageAt: new Date().toISOString(),
            lastMessagePreview: response.data.content.substring(0, 100)
          }
        })

        // Auto-generate title for new sessions
        if (state.messages.length <= 2) {
          this.dispatch('aiAgent/generateSessionTitle', state.currentSession.id)
        }

        return response.data
      } catch (error) {
        console.error('Error sending message:', error)

        // Add error message
        const errorMessage = new MessageModel({
          role: 'system',
          content: 'Sorry, I encountered an error processing your request. Please check your configuration and try again.',
          isError: true,
          createdAt: new Date().toISOString()
        })
        commit('ADD_MESSAGE', errorMessage)

        commit('SET_ERROR', 'Failed to send message')
        throw error
      } finally {
        commit('SET_SENDING_MESSAGE', false)
        commit('SET_TYPING_MESSAGE', '')
      }
    },

    async generateSessionTitle ({ commit }, sessionId) {
      try {
        const response = await ChatService.generateSessionTitle(sessionId)
        if (response.data.title) {
          commit('UPDATE_SESSION', {
            sessionId,
            updates: { title: response.data.title }
          })
        }
      } catch (error) {
        console.error('Error generating title:', error)
        // Not critical, don't show error to user
      }
    },

    // UI actions
    toggleChatSidebar ({ commit }) {
      commit('TOGGLE_CHAT_SIDEBAR')
    },

    toggleAgentPanel ({ commit }) {
      commit('TOGGLE_AGENT_PANEL')
    },

    saveDraft ({ commit }, draft) {
      commit('SET_MESSAGE_DRAFT', draft)
    },

    // Settings actions
    saveApiKey ({ commit }, { provider, apiKey }) {
      commit('SET_USER_API_KEY', { provider, apiKey })
    },

    setDefaultProvider ({ commit }, provider) {
      commit('SET_DEFAULT_PROVIDER', provider)
    },

    // Project actions
    async loadProjectsByAgent ({ commit }, agentId) {
      commit('SET_PROJECTS_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await ProjectService.getProjectsByAgent(agentId)
        commit('SET_PROJECTS', response.data)
        return response.data
      } catch (error) {
        console.error('Error loading projects:', error)
        commit('SET_ERROR', 'Failed to load projects')
        throw error
      } finally {
        commit('SET_PROJECTS_LOADING', false)
      }
    },

    async loadProject ({ commit }, { agentId, projectId }) {
      commit('CLEAR_ERROR')

      try {
        const response = await ProjectService.getProjectById(agentId, projectId)
        commit('SET_CURRENT_PROJECT', response.data)
        return response.data
      } catch (error) {
        console.error('Error loading project:', error)
        commit('SET_ERROR', 'Failed to load project')
        throw error
      }
    },

    async createProject ({ commit }, { agentId, projectData }) {
      commit('CLEAR_ERROR')

      try {
        const response = await ProjectService.createProject(agentId, projectData)
        const project = response.data

        commit('ADD_PROJECT', project)
        return project
      } catch (error) {
        console.error('Error creating project:', error)
        commit('SET_ERROR', 'Failed to create project')
        throw error
      }
    },

    async updateProject ({ commit }, { agentId, projectId, projectData }) {
      commit('CLEAR_ERROR')

      try {
        const response = await ProjectService.updateProject(agentId, projectId, projectData)
        const project = response.data

        commit('UPDATE_PROJECT', project)
        return project
      } catch (error) {
        console.error('Error updating project:', error)
        commit('SET_ERROR', 'Failed to update project')
        throw error
      }
    },

    async deleteProject ({ commit }, { agentId, projectId }) {
      commit('CLEAR_ERROR')

      try {
        await ProjectService.deleteProject(agentId, projectId)
        commit('REMOVE_PROJECT', projectId)
      } catch (error) {
        console.error('Error deleting project:', error)
        commit('SET_ERROR', 'Failed to delete project')
        throw error
      }
    },

    async loadProjectSessions ({ commit }, { agentId, projectId }) {
      commit('CLEAR_ERROR')

      try {
        const response = await ProjectService.getProjectSessions(agentId, projectId)
        commit('SET_PROJECT_SESSIONS', response.data)
        return response.data
      } catch (error) {
        console.error('Error loading project sessions:', error)
        commit('SET_ERROR', 'Failed to load project sessions')
        throw error
      }
    },

    async loadProjectKBs ({ commit }, { agentId, projectId }) {
      commit('CLEAR_ERROR')

      try {
        const response = await ProjectService.getProjectKnowledgeBases(agentId, projectId)
        commit('SET_PROJECT_KBS', response.data)
        return response.data
      } catch (error) {
        console.error('Error loading project knowledge bases:', error)
        commit('SET_ERROR', 'Failed to load project knowledge bases')
        throw error
      }
    },

    clearProjectData ({ commit }) {
      commit('CLEAR_PROJECT_DATA')
    },

    // Knowledge Base actions
    async createProjectKB ({ commit }, { agentId, projectId, kbData }) {
      commit('CLEAR_ERROR')

      try {
        const response = await KnowledgeBaseService.createProjectKnowledgeBase(agentId, projectId, kbData)
        const kb = response.data

        // Add to project KBs list
        const currentKBs = this.state.aiAgent.projectKBs || []
        commit('SET_PROJECT_KBS', [...currentKBs, kb])

        // Update project stats
        if (this.state.aiAgent.currentProject) {
          const updatedProject = Object.assign({}, this.state.aiAgent.currentProject)
          updatedProject.totalKnowledgeBases = (updatedProject.totalKnowledgeBases || 0) + 1
          commit('SET_CURRENT_PROJECT', updatedProject)
        }

        return kb
      } catch (error) {
        console.error('Error creating project KB:', error)
        commit('SET_ERROR', 'Failed to create knowledge base')
        throw error
      }
    },

    async updateProjectKB ({ commit }, { agentId, knowledgeBaseId, kbData }) {
      commit('CLEAR_ERROR')

      try {
        const response = await KnowledgeBaseService.updateKnowledgeBase(agentId, knowledgeBaseId, kbData)
        const updatedKB = response.data

        // Update in project KBs list
        const currentKBs = this.state.aiAgent.projectKBs || []
        const index = currentKBs.findIndex(kb => kb.id === knowledgeBaseId)
        if (index !== -1) {
          const newKBs = [...currentKBs]
          newKBs[index] = updatedKB
          commit('SET_PROJECT_KBS', newKBs)
        }

        return updatedKB
      } catch (error) {
        console.error('Error updating project KB:', error)
        commit('SET_ERROR', 'Failed to update knowledge base')
        throw error
      }
    },

    async deleteProjectKB ({ commit }, { agentId, knowledgeBaseId }) {
      commit('CLEAR_ERROR')

      try {
        await KnowledgeBaseService.deleteKnowledgeBase(agentId, knowledgeBaseId)

        // Remove from project KBs list
        const currentKBs = this.state.aiAgent.projectKBs || []
        commit('SET_PROJECT_KBS', currentKBs.filter(kb => kb.id !== knowledgeBaseId))

        // Update project stats
        if (this.state.aiAgent.currentProject) {
          const updatedProject = Object.assign({}, this.state.aiAgent.currentProject)
          updatedProject.totalKnowledgeBases = Math.max(0, (updatedProject.totalKnowledgeBases || 0) - 1)
          commit('SET_CURRENT_PROJECT', updatedProject)
        }
      } catch (error) {
        console.error('Error deleting project KB:', error)
        commit('SET_ERROR', 'Failed to delete knowledge base')
        throw error
      }
    },

    // ==================== NEW: WORKFLOW ACTIONS ====================
    async loadWorkflowsByProject ({ commit }, projectId) {
      commit('SET_WORKFLOWS_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const workflows = await WorkflowService.getWorkflowsByProject(projectId)
        commit('SET_WORKFLOWS', workflows)
        return workflows
      } catch (error) {
        console.error('Error loading workflows:', error)
        commit('SET_ERROR', 'Failed to load workflows')
        throw error
      } finally {
        commit('SET_WORKFLOWS_LOADING', false)
      }
    },

    async loadWorkflow ({ commit }, { projectId, workflowId }) {
      commit('CLEAR_ERROR')

      try {
        const workflow = await WorkflowService.getWorkflowById(projectId, workflowId)
        commit('SET_CURRENT_WORKFLOW', workflow)
        return workflow
      } catch (error) {
        console.error('Error loading workflow:', error)
        commit('SET_ERROR', 'Failed to load workflow')
        throw error
      }
    },

    async createWorkflow ({ commit }, { projectId, workflowData }) {
      commit('CLEAR_ERROR')

      try {
        const workflow = await WorkflowService.createWorkflow(projectId, workflowData)
        commit('ADD_WORKFLOW', workflow)
        return workflow
      } catch (error) {
        console.error('Error creating workflow:', error)
        commit('SET_ERROR', 'Failed to create workflow')
        throw error
      }
    },

    async updateWorkflow ({ commit }, { projectId, workflowId, workflowData }) {
      commit('CLEAR_ERROR')

      try {
        const workflow = await WorkflowService.updateWorkflow(projectId, workflowId, workflowData)
        commit('UPDATE_WORKFLOW', workflow)
        return workflow
      } catch (error) {
        console.error('Error updating workflow:', error)
        commit('SET_ERROR', 'Failed to update workflow')
        throw error
      }
    },

    async deleteWorkflow ({ commit }, { projectId, workflowId }) {
      commit('CLEAR_ERROR')

      try {
        await WorkflowService.deleteWorkflow(projectId, workflowId)
        commit('REMOVE_WORKFLOW', workflowId)
      } catch (error) {
        console.error('Error deleting workflow:', error)
        commit('SET_ERROR', 'Failed to delete workflow')
        throw error
      }
    },

    async duplicateWorkflow ({ commit }, { projectId, workflowId }) {
      commit('CLEAR_ERROR')

      try {
        const workflow = await WorkflowService.duplicateWorkflow(projectId, workflowId)
        commit('ADD_WORKFLOW', workflow)
        return workflow
      } catch (error) {
        console.error('Error duplicating workflow:', error)
        commit('SET_ERROR', 'Failed to duplicate workflow')
        throw error
      }
    },

    async executeWorkflow ({ dispatch }, { projectId, workflowId, executionData }) {
      try {
        const execution = await WorkflowService.executeWorkflow(projectId, workflowId, executionData)
        // Refresh executions list
        dispatch('loadWorkflowExecutions', { projectId, workflowId })
        return execution
      } catch (error) {
        console.error('Error executing workflow:', error)
        throw error
      }
    },

    async loadWorkflowExecutions ({ commit }, { projectId, workflowId }) {
      commit('CLEAR_ERROR')

      try {
        const executions = await WorkflowService.getWorkflowExecutions(projectId, workflowId)
        commit('SET_WORKFLOW_EXECUTIONS', executions)
        return executions
      } catch (error) {
        console.error('Error loading executions:', error)
        commit('SET_ERROR', 'Failed to load executions')
        throw error
      }
    },

    async loadExecutionById ({ commit }, { projectId, workflowId, executionId }) {
      commit('CLEAR_ERROR')

      try {
        const execution = await WorkflowService.getExecutionById(projectId, workflowId, executionId)
        return execution
      } catch (error) {
        console.error('Error loading execution:', error)
        commit('SET_ERROR', 'Failed to load execution')
        throw error
      }
    },

    async cancelExecution ({ dispatch }, { projectId, workflowId, executionId }) {
      try {
        await WorkflowService.cancelExecution(projectId, workflowId, executionId)
        // Refresh executions list
        dispatch('loadWorkflowExecutions', { projectId, workflowId })
      } catch (error) {
        console.error('Error cancelling execution:', error)
        throw error
      }
    },

    // ==================== NEW: FORM ACTIONS ====================
    async loadFormsByProject ({ commit }, projectId) {
      commit('SET_FORMS_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const forms = await FormService.getFormsByProject(projectId)
        commit('SET_FORMS', forms)
        return forms
      } catch (error) {
        console.error('Error loading forms:', error)
        commit('SET_ERROR', 'Failed to load forms')
        throw error
      } finally {
        commit('SET_FORMS_LOADING', false)
      }
    },

    async loadForm ({ commit }, { projectId, formId }) {
      commit('CLEAR_ERROR')

      try {
        const form = await FormService.getFormById(projectId, formId)
        commit('SET_CURRENT_FORM', form)
        return form
      } catch (error) {
        console.error('Error loading form:', error)
        commit('SET_ERROR', 'Failed to load form')
        throw error
      }
    },

    async createForm ({ commit }, { projectId, formData }) {
      commit('CLEAR_ERROR')

      try {
        const form = await FormService.createForm(projectId, formData)
        commit('ADD_FORM', form)
        return form
      } catch (error) {
        console.error('Error creating form:', error)
        commit('SET_ERROR', 'Failed to create form')
        throw error
      }
    },

    async updateForm ({ commit }, { projectId, formId, formData }) {
      commit('CLEAR_ERROR')

      try {
        const form = await FormService.updateForm(projectId, formId, formData)
        commit('UPDATE_FORM', form)
        return form
      } catch (error) {
        console.error('Error updating form:', error)
        commit('SET_ERROR', 'Failed to update form')
        throw error
      }
    },

    async deleteForm ({ commit }, { projectId, formId }) {
      commit('CLEAR_ERROR')

      try {
        await FormService.deleteForm(projectId, formId)
        commit('REMOVE_FORM', formId)
      } catch (error) {
        console.error('Error deleting form:', error)
        commit('SET_ERROR', 'Failed to delete form')
        throw error
      }
    },

    async loadFormSubmissions ({ commit }, { projectId, formId }) {
      commit('CLEAR_ERROR')

      try {
        const submissions = await FormService.getFormSubmissions(projectId, formId)
        commit('SET_FORM_SUBMISSIONS', submissions)
        return submissions
      } catch (error) {
        console.error('Error loading submissions:', error)
        commit('SET_ERROR', 'Failed to load submissions')
        throw error
      }
    },

    async deleteSubmission ({ commit, dispatch }, { projectId, formId, submissionId }) {
      commit('CLEAR_ERROR')

      try {
        await FormService.deleteSubmission(projectId, formId, submissionId)

        // Reload submissions after deletion
        dispatch('loadFormSubmissions', { projectId, formId })
      } catch (error) {
        console.error('Error deleting submission:', error)
        commit('SET_ERROR', 'Failed to delete submission')
        throw error
      }
    },

    // ==================== NEW: INTEGRATION ACTIONS ====================
    async loadIntegrationsByProject ({ commit }, projectId) {
      commit('SET_INTEGRATIONS_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const integrations = await IntegrationService.getIntegrationsByProject(projectId)
        commit('SET_INTEGRATIONS', integrations)
        return integrations
      } catch (error) {
        console.error('Error loading integrations:', error)
        commit('SET_ERROR', 'Failed to load integrations')
        throw error
      } finally {
        commit('SET_INTEGRATIONS_LOADING', false)
      }
    },

    async loadIntegration ({ commit }, { projectId, integrationId }) {
      commit('CLEAR_ERROR')

      try {
        const integration = await IntegrationService.getIntegrationById(projectId, integrationId)
        commit('SET_CURRENT_INTEGRATION', integration)
        return integration
      } catch (error) {
        console.error('Error loading integration:', error)
        commit('SET_ERROR', 'Failed to load integration')
        throw error
      }
    },

    async createIntegration ({ commit }, { projectId, integrationData }) {
      commit('CLEAR_ERROR')

      try {
        const integration = await IntegrationService.createIntegration(projectId, integrationData)
        commit('ADD_INTEGRATION', integration)
        return integration
      } catch (error) {
        console.error('Error creating integration:', error)
        commit('SET_ERROR', 'Failed to create integration')
        throw error
      }
    },

    async updateIntegration ({ commit }, { projectId, integrationId, integrationData }) {
      commit('CLEAR_ERROR')

      try {
        const integration = await IntegrationService.updateIntegration(projectId, integrationId, integrationData)
        commit('UPDATE_INTEGRATION', integration)
        return integration
      } catch (error) {
        console.error('Error updating integration:', error)
        commit('SET_ERROR', 'Failed to update integration')
        throw error
      }
    },

    async deleteIntegration ({ commit }, { projectId, integrationId }) {
      commit('CLEAR_ERROR')

      try {
        await IntegrationService.deleteIntegration(projectId, integrationId)
        commit('REMOVE_INTEGRATION', integrationId)
      } catch (error) {
        console.error('Error deleting integration:', error)
        commit('SET_ERROR', 'Failed to delete integration')
        throw error
      }
    },

    async testIntegration (context, { projectId, integrationId }) {
      try {
        return await IntegrationService.testIntegration(projectId, integrationId)
      } catch (error) {
        console.error('Error testing integration:', error)
        throw error
      }
    },

    async loadIntegrationLogs ({ commit }, { projectId, integrationId, limit }) {
      commit('CLEAR_ERROR')

      try {
        const logs = await IntegrationService.getIntegrationLogs(projectId, integrationId, limit)
        commit('SET_INTEGRATION_LOGS', logs)
        return logs
      } catch (error) {
        console.error('Error loading logs:', error)
        commit('SET_ERROR', 'Failed to load logs')
        throw error
      }
    },

    // ==================== NEW: DASHBOARD ACTIONS ====================
    async loadDashboard ({ commit }, { projectId, days }) {
      commit('SET_DASHBOARD_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const dashboard = await ExecutionService.getDashboard(projectId, days)
        commit('SET_DASHBOARD', dashboard)
        return dashboard
      } catch (error) {
        console.error('Error loading dashboard:', error)
        commit('SET_ERROR', 'Failed to load dashboard')
        throw error
      } finally {
        commit('SET_DASHBOARD_LOADING', false)
      }
    },

    async loadIntegrationHealth ({ commit }, projectId) {
      commit('CLEAR_ERROR')

      try {
        const health = await ExecutionService.getIntegrationHealth(projectId)
        commit('SET_INTEGRATION_HEALTH', health)
        return health
      } catch (error) {
        console.error('Error loading integration health:', error)
        commit('SET_ERROR', 'Failed to load integration health')
        throw error
      }
    },

    /**
     * Load users for a project (for database recipient selector)
     */
    loadProjectUsers (context, projectId) {
      return new Promise(function (resolve, reject) {
        // You'll need to create this API endpoint
        // For now, returning mock data structure

        // TODO: Replace with actual API call when endpoint is ready
        // Example API call structure:
        /*
        axios.get('/api/projects/' + projectId + '/users')
          .then(function (response) {
            resolve(response.data)
          })
          .catch(function (error) {
            reject(error)
          })
        */

        // TEMPORARY: Mock data for development
        // Remove this when API endpoint is ready
        var mockUsers = [
          { id: 1, name: 'John Doe', email: 'john@example.com', phone: '555-0001', role: 'Admin' },
          { id: 2, name: 'Jane Smith', email: 'jane@example.com', phone: '555-0002', role: 'Manager' },
          { id: 3, name: 'Bob Johnson', email: 'bob@example.com', phone: '555-0003', role: 'Reviewer' },
          { id: 4, name: 'Alice Williams', email: 'alice@example.com', phone: '555-0004', role: 'Approver' },
          { id: 5, name: 'Charlie Brown', email: 'charlie@example.com', phone: '555-0005', role: 'Manager' }
        ]

        // Simulate API delay
        setTimeout(function () {
          resolve(mockUsers)
        }, 500)
      })
    }
  },

  getters: {
    // ==================== EXISTING GETTERS ====================
    // Agent getters
    activeAgents: (state) => state.agents.filter(a => a.isActive),

    getAgentById: (state) => (id) => state.agents.find(a => a.id === id),

    agentsByProvider: (state) => (provider) =>
      state.agents.filter(a => a.llmProvider === provider),

    // Session getters
    currentAgentSessions: (state) => {
      if (!state.currentAgent) return []
      return state.sessions.filter(s => s.agentId === state.currentAgent.id)
    },

    recentSessions: (state) =>
      [...state.sessions]
        .sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt))
        .slice(0, 10),

    // Message getters
    hasMessages: (state) => state.messages.length > 0,

    lastMessage: (state) =>
      state.messages.length > 0
        ? state.messages[state.messages.length - 1]
        : null,

    conversationTokenCount: (state) =>
      state.messages.reduce((sum, msg) => sum + (msg.tokenCount || 0), 0),

    conversationCost: (state) =>
      state.messages.reduce((sum, msg) => sum + (msg.cost || 0), 0),

    // UI getters
    isLoading: (state) =>
      state.agentsLoading || state.sessionsLoading ||
      state.messagesLoading || state.sendingMessage,

    canSendMessage: (state) =>
      state.currentSession &&
      state.currentAgent &&
      state.currentAgent.isActive &&
      !state.sendingMessage &&
      state.typingMessage.trim().length > 0,

    // Settings getters
    hasApiKey: (state) => (provider) =>
      !!state.userApiKeys[provider],

    currentApiKey: (state) =>
      state.userApiKeys[state.defaultProvider] || null,

    // Project getters
    getProjectsByAgentId: (state) => (agentId) => {
      return state.projects.filter(p => p.agentId === agentId)
    },

    activeProjects: (state) => state.projects.filter(p => p.isActive),

    projectCount: (state) => state.projects.length,

    currentAgentProjects: (state) => {
      if (!state.currentAgent) return []
      return state.projects.filter(p => p.agentId === state.currentAgent.id)
    },

    // ==================== NEW: WORKFLOW GETTERS ====================
    workflows: (state) => state.workflows,

    currentWorkflow: (state) => state.currentWorkflow,

    workflowExecutions: (state) => state.workflowExecutions,

    getWorkflowById: (state) => (id) => {
      return state.workflows.find(w => w.id === id)
    },

    activeWorkflows: (state) => {
      return state.workflows.filter(w => w.status === 'Active')
    },

    draftWorkflows: (state) => {
      return state.workflows.filter(w => w.status === 'Draft')
    },

    workflowCount: (state) => state.workflows.length,

    // ==================== NEW: FORM GETTERS ====================
    forms: (state) => state.forms,

    currentForm: (state) => state.currentForm,

    formSubmissions: (state) => state.formSubmissions,

    getFormById: (state) => (id) => {
      return state.forms.find(f => f.id === id)
    },

    formCount: (state) => state.forms.length,

    // ==================== NEW: INTEGRATION GETTERS ====================
    integrations: (state) => state.integrations,

    currentIntegration: (state) => state.currentIntegration,

    integrationLogs: (state) => state.integrationLogs,

    getIntegrationById: (state) => (id) => {
      return state.integrations.find(i => i.id === id)
    },

    activeIntegrations: (state) => {
      return state.integrations.filter(i => i.isActive)
    },

    integrationCount: (state) => state.integrations.length,

    // ==================== NEW: DASHBOARD GETTERS ====================
    dashboard: (state) => state.dashboard,

    integrationHealth: (state) => state.integrationHealth,

    isDashboardLoading: (state) => state.dashboardLoading
  }
}
